// Side pane listing today's jobs with live "statusing": who is checked in, who
// is on site, who is on the road and who hasn't shown up yet.
import { useEffect, useRef, useState } from 'react';

import {
  ChevronDownIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/16/solid';
import clsx from 'clsx';

import { JobDetailsLink } from '@/components/LiveMap/JobSelection';
import {
  describeActivity,
  getStatusLabel,
} from '@/components/LiveMap/liveMapFormat';
import {
  JobStatusSummary,
  LiveMapJob,
  LiveMapOperator,
  ReceiverStatus,
  TruckLocation,
  formatTimeAgo,
  getIsActiveAssignmentStatus,
  getIsLocationStale,
  getJobStatusSummary,
  getTruckActivity,
} from '@/components/LiveMap/liveMapModel';
import PinToggle, { PinIcon } from '@/components/common/PinToggle';

const STATUS_ORDER: Record<ReceiverStatus, number> = {
  checkedIn: 0,
  acceptedNotCheckedIn: 1,
  accepted: 1,
  notified: 2,
  'not notified': 3,
  checkedOut: 4,
  declined: 5,
};

type JobsPaneProps = {
  title: string;
  jobs: LiveMapJob[];
  operatorsByJobId: Record<string, LiveMapOperator[]>;
  locations: Record<string, TruckLocation>;
  pinnedJobIds: string[];
  highlightedJobIds: string[];
  selectedTruckUid: string | null;
  cameFromSiteId: string | null;
  isLoading: boolean;
  nowMs: number;
  onTogglePin: (jobId: string) => void;
  onShowAll: () => void;
  onSelectTruck: (uid: string, jobId: string) => void;
};

/**
 * List of the day's jobs. Pinning jobs narrows the map (and the list view) to
 * their trucks and sites; selecting a truck on the map highlights the jobs it
 * is working.
 * @param {JobsPaneProps} props
 * @returns {React.JSX.Element}
 */
export default function LiveMapJobsPane({
  title,
  jobs,
  operatorsByJobId,
  locations,
  pinnedJobIds,
  highlightedJobIds,
  selectedTruckUid,
  cameFromSiteId,
  isLoading,
  nowMs,
  onTogglePin,
  onShowAll,
  onSelectTruck,
}: JobsPaneProps) {
  return (
    <div className="live-map-jobs-pane flex h-full flex-col">
      <div className="border-base-200 flex items-center justify-between gap-2 border-b py-2 pr-10 pl-3">
        <div>
          <div className="font-bold">{title}</div>
          {pinnedJobIds.length ? (
            <PinnedSummary
              pinnedCount={pinnedJobIds.length}
              totalCount={jobs.length}
              onShowAll={onShowAll}
            />
          ) : (
            <div className="text-xs opacity-70">
              {'Pin jobs to focus the map and the list'}
            </div>
          )}
        </div>
      </div>
      <ul className="flex-1 space-y-2 overflow-y-auto p-2">
        {isLoading
          ? Array.from({ length: 4 }).map((_, idx) => (
              <li
                key={idx}
                className="skeleton bg-base-200 h-28 w-full rounded-xl"
              />
            ))
          : jobs.map((job) => (
              <LiveMapJobCard
                key={job.id}
                job={job}
                operators={operatorsByJobId[job.id] || []}
                locations={locations}
                isPinned={pinnedJobIds.includes(job.id)}
                isHighlighted={highlightedJobIds.includes(job.id)}
                selectedTruckUid={selectedTruckUid}
                cameFromSiteId={cameFromSiteId}
                nowMs={nowMs}
                onTogglePin={() => onTogglePin(job.id)}
                onSelectTruck={onSelectTruck}
              />
            ))}
        {!isLoading && !jobs.length && (
          <li className="p-4 text-center text-sm opacity-70">
            {'No jobs scheduled for this day.'}
          </li>
        )}
      </ul>
    </div>
  );
}

/**
 * "N of M pinned" with a button back to all the jobs. Shown at the top of the
 * jobs pane and, while the pane is collapsed, next to the Jobs button.
 * @param {object} props
 * @param {number} props.pinnedCount - Pinned jobs on the map.
 * @param {number} props.totalCount - Jobs on the day.
 * @param {() => void} props.onShowAll - Unpin all jobs.
 * @param {string} [props.className]
 * @returns {React.JSX.Element}
 */
export function PinnedSummary({
  pinnedCount,
  totalCount,
  onShowAll,
  className,
}: {
  pinnedCount: number;
  totalCount: number;
  onShowAll: () => void;
  className?: string;
}) {
  return (
    <div className={clsx('flex items-center gap-2 text-xs', className)}>
      <span className="flex items-center gap-1">
        <PinIcon className="size-3.5" />
        {`${pinnedCount} of ${totalCount} pinned`}
      </span>
      <button
        type="button"
        className="btn btn-xs bg-primary-dark border-primary-dark hover:bg-primary-dark/85 text-white"
        onClick={onShowAll}
        title="Unpin all jobs and show every job"
      >
        {'Show all'}
      </button>
    </div>
  );
}

/**
 * One job with its sites, statusing summary and operators.
 * @returns {React.JSX.Element}
 */
function LiveMapJobCard({
  job,
  operators,
  locations,
  isPinned,
  isHighlighted,
  selectedTruckUid,
  cameFromSiteId,
  nowMs,
  onTogglePin,
  onSelectTruck,
}: {
  job: LiveMapJob;
  operators: LiveMapOperator[];
  locations: Record<string, TruckLocation>;
  isPinned: boolean;
  isHighlighted: boolean;
  selectedTruckUid: string | null;
  cameFromSiteId: string | null;
  nowMs: number;
  onTogglePin: () => void;
  onSelectTruck: (uid: string, jobId: string) => void;
}) {
  const cardRef = useRef<HTMLLIElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const showOperators = isExpanded || isHighlighted;
  const summary = getJobStatusSummary(job, operators, locations, nowMs);
  const hasStarted = job.startDatetime.getTime() <= nowMs;
  const notCheckedIn = summary.accepted + summary.pending;

  // Bring the job into view when a truck working it is selected on the map
  useEffect(() => {
    if (isHighlighted) {
      cardRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [isHighlighted, selectedTruckUid]);

  return (
    <li
      ref={cardRef}
      className={clsx(
        'live-map-job-card card bg-base-100 border-base-200 border shadow-sm transition-shadow',
        isPinned && 'ring-accent ring-2',
        isHighlighted && 'ring-info shadow-lg ring-4'
      )}
    >
      <div className="flex gap-1.5 p-2.5 pl-1.5">
        <PinToggle
          isPinned={isPinned}
          onToggle={onTogglePin}
          label={job.name}
          color={job.color}
        />
        {/* Clicking anywhere on the card (name included) pins the job; only
            the icon opens the job details in a new tab */}
        <div
          className="min-w-0 flex-1 cursor-pointer"
          onClick={onTogglePin}
          title={
            isPinned
              ? 'Unpin this job'
              : 'Pin this job to focus the map and the list on it'
          }
        >
          <div className="flex items-start justify-between gap-2">
            <span className="line-clamp-2 leading-tight font-bold">
              {job.name}
            </span>
            <span className="flex shrink-0 items-start gap-1">
              <span className="text-xs opacity-70">
                {job.startDatetime.getTimeStr()}
              </span>
              <JobDetailsLink jobId={job.id} jobName={job.name} />
            </span>
          </div>
          {!!job.clientName && (
            <div className="truncate text-xs opacity-70">{job.clientName}</div>
          )}
          <JobSitesLine
            job={job}
            cameFromSiteId={cameFromSiteId}
            isHighlighted={isHighlighted}
            summary={summary}
          />
          <JobStatusing
            summary={summary}
            notCheckedIn={notCheckedIn}
            hasStarted={hasStarted}
          />
        </div>
      </div>
      {!!operators.length && (
        <>
          <button
            type="button"
            className="btn btn-ghost btn-xs border-base-200 w-full rounded-none border-t"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={showOperators}
          >
            {`${operators.length} ${operators.length === 1 ? 'operator' : 'operators'}`}
            <ChevronDownIcon
              className={clsx(
                'size-4 transition-transform',
                showOperators && 'rotate-180'
              )}
            />
          </button>
          {showOperators && (
            <ul className="border-base-200 border-t">
              {[...operators]
                .sort(
                  (a, b) =>
                    STATUS_ORDER[getStatusOnJob(a, job.id)] -
                    STATUS_ORDER[getStatusOnJob(b, job.id)]
                )
                .map((operator) => (
                  <OperatorRow
                    key={operator.uid}
                    operator={operator}
                    job={job}
                    location={locations[operator.uid]}
                    isSelected={operator.uid === selectedTruckUid}
                    nowMs={nowMs}
                    onSelect={() => onSelectTruck(operator.uid, job.id)}
                  />
                ))}
            </ul>
          )}
        </>
      )}
    </li>
  );
}

/**
 * The operator's status on a specific job.
 * @param {LiveMapOperator} operator
 * @param {string} jobId
 * @returns {ReceiverStatus}
 */
function getStatusOnJob(
  operator: LiveMapOperator,
  jobId: string
): ReceiverStatus {
  return (
    operator.assignments.find((assignment) => assignment.jobId === jobId)
      ?.status || 'not notified'
  );
}

/**
 * "A Quarry → B Dump" with live truck counts per site.
 * @returns {React.JSX.Element | null}
 */
function JobSitesLine({
  job,
  cameFromSiteId,
  isHighlighted,
  summary,
}: {
  job: LiveMapJob;
  cameFromSiteId: string | null;
  isHighlighted: boolean;
  summary: JobStatusSummary;
}) {
  if (!job.sites.length) {
    return (
      <div className="mt-1 text-xs italic opacity-60">
        {'No site locations'}
      </div>
    );
  }
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-1 gap-y-0.5 text-xs">
      {job.sites.map((site, idx) => {
        const onSite = summary.onSiteBySiteId[site.id] || 0;
        const isCameFrom = isHighlighted && site.id === cameFromSiteId;
        return (
          <span key={site.id} className="inline-flex items-center gap-1">
            {/* Only a two-site job has a known direction (A -> B) */}
            {idx > 0 && (
              <span className="opacity-50">
                {job.sites.length === 2 ? '→' : '·'}
              </span>
            )}
            <span
              className={clsx(
                'inline-flex max-w-44 items-center gap-1 rounded px-1',
                isCameFrom && 'bg-info text-info-content'
              )}
              title={`${site.role === 'load' ? 'Load site' : site.role === 'dump' ? 'Dump site' : 'Stop'}: ${site.name}${isCameFrom ? ' (selected truck came from here)' : ''}`}
            >
              <span
                className="rounded-sm px-1 font-bold text-white"
                style={{ background: job.color }}
              >
                {site.label}
              </span>
              <span className="truncate">{site.name}</span>
              {onSite > 0 && (
                <span className="badge badge-xs badge-success">{onSite}</span>
              )}
            </span>
          </span>
        );
      })}
    </div>
  );
}

/**
 * Check-in progress bar and counts for a job.
 * @returns {React.JSX.Element}
 */
function JobStatusing({
  summary,
  notCheckedIn,
  hasStarted,
}: {
  summary: JobStatusSummary;
  notCheckedIn: number;
  hasStarted: boolean;
}) {
  const expected = Math.max(summary.requested, summary.assigned);
  const unfilled = Math.max(summary.requested - summary.assigned, 0);
  const working = summary.checkedIn + summary.checkedOut;
  const segments = [
    { count: summary.checkedIn, className: 'bg-success', label: 'checked in' },
    { count: summary.checkedOut, className: 'bg-info', label: 'checked out' },
    {
      count: summary.accepted,
      className: 'bg-status-neutral',
      label: 'accepted, not checked in',
    },
    {
      count: summary.pending,
      className: 'bg-base-300/30',
      label: 'no response',
    },
    {
      count: unfilled,
      className: 'border border-dashed border-base-300/50',
      label: 'unfilled',
    },
  ].filter((segment) => segment.count > 0);
  const onSite = Object.values(summary.onSiteBySiteId).reduce(
    (total, count) => total + count,
    0
  );
  return (
    <div className="mt-2 space-y-1">
      <div
        className="flex h-2 w-full gap-0.5 overflow-hidden rounded-full"
        role="img"
        aria-label={segments.map((s) => `${s.count} ${s.label}`).join(', ')}
      >
        {segments.map((segment) => (
          <div
            key={segment.label}
            className={clsx('h-full rounded-full', segment.className)}
            style={{ flexGrow: segment.count }}
            title={`${segment.count} ${segment.label}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
        <span
          className="font-semibold"
          title="Trucks that have checked in today, including ones already checked out"
        >
          {`${working}/${expected || 0} checked in`}
        </span>
        {summary.checkedOut > 0 && (
          <span className="text-info">{`${summary.checkedOut} done`}</span>
        )}
        {onSite > 0 && (
          <span className="text-success">{`${onSite} on site`}</span>
        )}
        {summary.enRoute > 0 && <span>{`${summary.enRoute} en route`}</span>}
        {summary.noSignal > 0 && (
          <span className="text-error">{`${summary.noSignal} no signal`}</span>
        )}
        {notCheckedIn > 0 && (
          <span
            className={clsx(
              'inline-flex items-center gap-0.5',
              hasStarted && 'text-primary font-semibold'
            )}
          >
            {hasStarted && <ExclamationTriangleIcon className="size-3.5" />}
            {`${notCheckedIn} not checked in`}
          </span>
        )}
        {unfilled > 0 && (
          <span className="opacity-70">{`${unfilled} unfilled`}</span>
        )}
      </div>
    </div>
  );
}

/**
 * One operator on a job: status plus where their truck is right now.
 * @returns {React.JSX.Element}
 */
function OperatorRow({
  operator,
  job,
  location,
  isSelected,
  nowMs,
  onSelect,
}: {
  operator: LiveMapOperator;
  job: LiveMapJob;
  location: TruckLocation | undefined;
  isSelected: boolean;
  nowMs: number;
  onSelect: () => void;
}) {
  const status = getStatusOnJob(operator, job.id);
  const isStale = getIsLocationStale(location, nowMs);
  // Same rule as the map: only accepted or checked-in trucks are drawn, and a
  // truck with no recent signal only while it's checked in
  const isOnMap =
    !!location &&
    getIsActiveAssignmentStatus(status) &&
    (!isStale || status === 'checkedIn');
  const canSelect = isOnMap;
  const unselectableTitle = !location
    ? 'No location reported today'
    : !getIsActiveAssignmentStatus(status)
      ? 'Only accepted and checked-in trucks show on the map'
      : 'No recent signal, so not shown on the map';
  const detail = (() => {
    if (status === 'declined' || status === 'checkedOut') {
      return getStatusLabel(status);
    }
    if (!location) {
      return status === 'checkedIn'
        ? 'Checked in · no location today'
        : getStatusLabel(status);
    }
    const where = isStale
      ? `last seen ${formatTimeAgo(location.latest.timestamp, nowMs)}`
      : describeActivity(getTruckActivity(location, [job]));
    return status === 'checkedIn'
      ? where
      : `${getStatusLabel(status)} · ${where}`;
  })();
  const dotClassName: Record<ReceiverStatus, string> = {
    checkedIn: isStale ? 'bg-error' : 'bg-success',
    accepted: 'bg-status-neutral',
    acceptedNotCheckedIn: 'bg-status-neutral',
    notified: 'bg-base-300/30',
    'not notified': 'bg-base-300/30',
    checkedOut: 'bg-info',
    declined: 'bg-primary',
  };
  return (
    <li>
      <button
        type="button"
        className={clsx(
          'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs',
          canSelect ? 'hover:bg-base-200 cursor-pointer' : 'cursor-default',
          isSelected && 'bg-info/20'
        )}
        onClick={canSelect ? onSelect : undefined}
        disabled={!canSelect}
        title={canSelect ? 'Show on map' : unselectableTitle}
      >
        <span
          className={clsx('size-2 shrink-0 rounded-full', dotClassName[status])}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold">
            {!!operator.truckDisplayId && `${operator.truckDisplayId} · `}
            {operator.name}
          </span>
          <span className="block truncate opacity-70">{detail}</span>
        </span>
      </button>
    </li>
  );
}
